import { Component, Inject, OnInit } from '@angular/core';
import { Validators, ValidatorFn, AbstractControl, FormControl } from '@angular/forms';
import { deepCopy, DwActionPreference, DwComponent, DwComponentType, DwEventService, DwExpressionService, DwFormActionConfig, DwFormActionInterceptor, DwFormActionInterceptResult, DwFormActionKeys, DwMenuService, DwMessageSeverity, DwMetaDataFormStateService, DwMetaDataService, DwMetaDataServiceToken, DwOrmDataService, DwQueryFieldsHelper, DwSectionBaseComponent, DwSecurityUserService, DwUiConfigRegistryService, DwUIMetaDataConfig, DwUIMetaDataConfigToken, ensureArray } from '@devwareapps/devware-cap';
import { PopupTextCellEditor } from 'ag-grid-community';
import { of } from 'rxjs';
import { PoiValidatorsPoiStatus } from '../../meta-data/constants/poi-validators-poi-status.enum';
import { PoiValidatorsVehicleType } from '../../meta-data/constants/poi-validators-vehicle-type.enum';
import { ProofOfInsuranceEntity, ProofOfInsuranceMetaDataItemNames, ProofOfInsuranceMetaDataService } from '../../meta-data/proofofinsurance-meta-data.service';
import { debounceTime } from 'rxjs/operators';
import * as moment from 'moment';


@DwComponent(
  {
    key: 'poi-validators',
    name: 'POI Validator',
    componentType: DwComponentType.formSection,
    isGlobal: false,
    parentItemName: ProofOfInsuranceMetaDataItemNames.ProofOfInsurance,
  }
)
@Component({
  selector: 'app-poi-validators',
  templateUrl: './poi-validators.component.html',
  styleUrls: ['./poi-validators.component.scss']
})
export class PoiValidatorsComponent extends DwSectionBaseComponent {

  poi: ProofOfInsuranceEntity;

  constructor(private dwSecurityUserService: DwSecurityUserService,
    dwExpressionService: DwExpressionService,
    protected dwUiConfigRegistryService: DwUiConfigRegistryService,
    @Inject(DwUIMetaDataConfigToken) uiMetaDataConfig: DwUIMetaDataConfig,
    @Inject(DwMetaDataServiceToken) dwMetaDataService: DwMetaDataService,
    private dwMetaDataFormStateService: DwMetaDataFormStateService,
    private dwMenuService: DwMenuService,
    private dwEventService: DwEventService
  ) {
    super(dwExpressionService, dwUiConfigRegistryService, uiMetaDataConfig, dwMetaDataService);
  }

  setupComponentComplete(): void {
    this.dwMetaDataFormStateService.addActionInterceptor(this.getFormActionInterceptor())

    //Get the POI Summary Id from the menu
    const poiSummaryId = this.dwMenuService.menuState.currentParams.get("ProofOfInsuranceSummaryId");

    //Get or Create a POI Summary control on the form
    const poiSummaryFC = this.getOrCreateFormControl("ProofOfInsuranceSummaryId", this.formGroup);

    if (poiSummaryFC) {
      poiSummaryFC.setValue(poiSummaryId);
    }
  }

  getFormActionInterceptor(): DwFormActionInterceptor {

    const dwMetaDataFormStateService = this.dwMetaDataFormStateService;

    const interceptor: DwFormActionInterceptor = {
      interceptorName: 'poi-create-interceptor',
      onBeforeAction: (actionPref: DwActionPreference<DwFormActionConfig>, actionConfig: any) => {

        const result: DwFormActionInterceptResult = {
          abort: false
        }
        if (actionPref.actionKey != DwFormActionKeys.formSave && actionPref.actionKey != DwFormActionKeys.formWorkflow) {
          return of(result);
        }

        const poi: ProofOfInsuranceEntity = dwMetaDataFormStateService.state.formApi.getFormData();

        // Check the POI for validation errors
        const errors = this.getValidationErrors(poi);

        if (errors.length > 0) {
          // Set it to abort and dispaly a message (this can be a string or you can get fancy and return a DwMessage with more options)
          result.abort = true;
          result.message = {
            messageHeading: 'Please fix the following errors:',
            messageBody: this.buildErrorMessageHtml(errors), // `${errors.join(',')}`,
            severity: DwMessageSeverity.warning,
          }
        }
        return of(result);
      },
      onAfterAction: (actionPref: DwActionPreference<DwFormActionConfig>, actionConfig: any, actionResult: any) => {

        const result: DwFormActionInterceptResult = {
          abort: false
        }

        const isSave = actionPref.actionKey == DwFormActionKeys.formSave || actionPref.actionKey == DwFormActionKeys.formWorkflow;
       
        // Ensure this is after a form save
        if (!isSave) {
          return of(result);
        }

        // Publish Proof of Insurance Summary ORM Event so the form/grid refreshes
        this.dwEventService.publishEvent(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(ProofOfInsuranceMetaDataItemNames.ProofOfInsuranceSummary), {});

       return of(result);
      },

    }
    return interceptor;
  }

  private buildErrorMessageHtml(errors: string[]): string {
    const messagesHtml: string[] = [];

    for (const error of errors) {
      messagesHtml.push(`<li>${error}</li>`);
    }
    return `<ul>${messagesHtml.join('')}</ul>`;
  }

  getValidationErrors(poi: ProofOfInsuranceEntity): string[] {
    const errors: string[] = [];

    // Always require these fields (handles pending save which bypasses validation)
     if (!poi.VehicleTypeId) {
      errors.push('Vehicle Type is required');
    }

    if (poi.POIStatusId == PoiValidatorsPoiStatus.approved) {

      if (!poi.LicenseVerified) {
        errors.push('License verification is required');
      }
      if (!poi.SignedCorpFleetPolicy) {
        errors.push('Signed Corporate Fleet Policies is required');
      }

      if (poi.VehicleTypeId == PoiValidatorsVehicleType.personal) {
      
        if (!poi.InsuranceCompany) {
          errors.push('Insurance Company is required');
        }
        if (!poi.PolicyNumber) {
          errors.push('Policy Number is required');
        }
        if (!poi.PolicyTypeId) {
          errors.push('Policy Type is required');
        }
        if (!poi.IssuedTo) {
          errors.push('Issued To is required');
        }
        if (!poi.IssueDate) {
          errors.push('Issued Date is required');
        }
        if (!poi.CountryId) {
          errors.push('Country is required');
        }
        if (!poi.ExpirationDate) {
          errors.push('Expiration Date is required');
        }
        if (moment(poi.ExpirationDate).isBefore(moment())) {
          errors.push('Expiration Date must be after today');
        }
        if (!poi.StateProvinceId) {
          errors.push('State is required');
        }
      }
    }
    return errors;
  }
}